import { useState } from "react";
import SectionLabel from "@/components/base/SectionLabel";
import { dxAdvisoryData } from "@/mocks/dxAdvisoryData";

export default function CTASection() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string)?.trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt") {
        payload.append(key, value as string);
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d9ithrec26n1c7c5pvu0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore */
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed?.code === "OK" &&
        !serverMsg.includes("spam") &&
        !serverMsg.includes("form data is spam")
      ) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "送信に失敗しました。もう一度お試しください。");
      }
    } catch {
      setFormStatus("error");
      setFormError("送信に失敗しました。もう一度お試しください。");
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-100">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
          {/* Left: Text + points */}
          <div>
            <SectionLabel label={dxAdvisoryData.cta.label} className="mb-3" />
            <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4 md:mb-5">
              {dxAdvisoryData.cta.heading}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5 md:mb-6">
              {dxAdvisoryData.cta.description}
            </p>

            {/* Before-form points */}
            <div className="space-y-3 mb-5 md:mb-6">
              {dxAdvisoryData.cta.beforeFormPoints.map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-500" />
                  </div>
                  <span className="text-sm md:text-base text-foreground-700">{point}</span>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-md p-4 md:p-5 border border-background-200/70">
              <div className="flex items-start gap-2.5">
                <i className="ri-information-line text-primary-500 mt-0.5" />
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {dxAdvisoryData.cta.note}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-7">
            {formStatus === "success" ? (
              <div className="text-center py-8 md:py-10">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl md:text-3xl text-primary-600" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-foreground-900 mb-2 font-heading">
                  送信完了
                </h3>
                <p className="text-sm md:text-base text-foreground-700">
                  お問い合わせありがとうございます。担当者より数日以内にご連絡いたします。
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-foreground-800 mb-1.5">
                      お名前
                      <span className="text-primary-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-foreground-800 mb-1.5">
                      会社名・組織名
                    </label>
                    <input
                      type="text"
                      name="company"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                      placeholder="株式会社○○"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-foreground-800 mb-1.5">
                    メールアドレス
                    <span className="text-primary-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                    placeholder="example@company.com"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-foreground-800 mb-1.5">
                    問い合わせ種別
                  </label>
                  <select
                    name="inquiry_type"
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                  >
                    <option value="">選択してください</option>
                    {dxAdvisoryData.cta.inquiryTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-foreground-800 mb-1.5">
                    お問い合わせ内容
                    <span className="text-primary-500 ml-1">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors resize-none"
                    placeholder="ご相談内容や現状の課題をお聞かせください"
                  />
                  <p className="text-xs text-foreground-500 mt-1 text-right">
                    500文字以内
                  </p>
                </div>

                {/* Honeypot */}
                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus === "error" && formError && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  送信する
                  <i className="ri-send-plane-line" />
                </button>

                <p className="text-xs text-foreground-500 text-center">
                  いただいた情報は、お問い合わせ対応のみに使用します。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}